import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Crown, Heart, Sparkles, Eye, Infinity, Users, TrendingUp, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

export default function PremiumModal({ isOpen, onClose, onUpgradeSuccess, currentPlan = 'free', triggerReason = '' }) {
  // Pre-select Gold when triggered by "who_liked" (Gold-only feature)
  const [selectedPlan, setSelectedPlan] = useState(triggerReason === 'who_liked' ? 'gold' : 'gold');
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!isOpen) return null;

  const TRIGGER_CONTENT = {
    like_limit: { headline: t('premium.headline_like_limit'), subtitle: t('premium.subtitle_like_limit'), icon: Heart },
    who_liked: { headline: t('premium.headline_who_liked'), subtitle: t('premium.subtitle_who_liked'), icon: Eye, forceGold: true },
    post_match: { headline: t('premium.headline_post_match'), subtitle: t('premium.subtitle_post_match'), icon: Sparkles },
    missed_match: { headline: t('premium.headline_missed'), subtitle: t('premium.subtitle_missed'), icon: Heart },
    filter: { headline: t('premium.headline_filter'), subtitle: t('premium.subtitle_filter'), icon: Sparkles },
    default: { headline: t('premium.headline_default'), subtitle: t('premium.subtitle_default'), icon: Crown },
  };

  const PLANS = {
    premium: {
      name: t('premium.premium_name'), price: t('premium.premium_price'), period: t('premium.per_month'),
      color: '#6E1A24', icon: Heart, tagline: t('premium.premium_tagline'),
      benefits: [
        { text: t('premium.benefit_unlimited'), icon: Infinity, bold: true },
        { text: t('premium.benefit_filters'), icon: Sparkles },
        { text: t('premium.benefit_more_matches'), icon: Heart },
      ]
    },
    gold: {
      name: t('premium.gold_name'), price: t('premium.gold_price'), period: t('premium.per_month'),
      color: '#D4AF37', icon: Crown, popular: true, tagline: t('premium.gold_tagline'),
      benefits: [
        { text: t('premium.benefit_everything_premium'), icon: Check },
        { text: t('premium.benefit_see_likes'), icon: Eye, bold: true },
        { text: t('premium.benefit_gold_badge'), icon: Crown, bold: true },
        { text: t('premium.benefit_more_visible'), icon: TrendingUp, bold: true },
        { text: t('premium.benefit_higher_chance'), icon: Star },
      ]
    }
  };

  const trigger = TRIGGER_CONTENT[triggerReason] || TRIGGER_CONTENT.default;
  const TriggerIcon = trigger.icon;

  const handleUpgrade = () => {
    onClose();
    navigate(`/checkout?plan=${selectedPlan}`);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="premium-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal — slides up on mobile, centered on desktop */}
      <div className="relative w-full max-w-lg bg-[#FAF9F6] sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          data-testid="premium-modal-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>

        {/* Emotional Header */}
        <div className="relative bg-gradient-to-br from-[#6E1A24] via-[#8A2331] to-[#6E1A24] px-6 pt-10 pb-8 text-center text-white overflow-hidden">
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <TriggerIcon className="w-8 h-8" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium mb-2 leading-tight">{trigger.headline}</h2>
            <p className="text-white/75 text-sm max-w-xs mx-auto leading-relaxed">{trigger.subtitle}</p>
          </div>
        </div>

        {/* Plan cards */}
        <div className="px-5 py-5 space-y-3">
          {Object.entries(PLANS).map(([key, plan]) => {
            const isSelected = selectedPlan === key;
            const isCurrentPlan = currentPlan === key;
            const isGoldOnly = trigger.forceGold && key === 'premium';
            const isDisabled = isCurrentPlan || isGoldOnly;
            const PlanIcon = plan.icon;

            return (
              <button
                key={key}
                onClick={() => !isDisabled && setSelectedPlan(key)}
                disabled={isDisabled}
                className={`relative w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${
                  isDisabled
                    ? 'border-[#E5E3DB] bg-[#F5F2EB] opacity-50 cursor-not-allowed'
                    : isSelected
                    ? key === 'gold' ? 'border-[#D4AF37] bg-[#D4AF37]/5 shadow-lg shadow-[#D4AF37]/10' : 'border-[#6E1A24] bg-[#6E1A24]/5 shadow-md'
                    : 'border-[#E5E3DB] hover:border-[#6E1A24]/30 hover:shadow-sm'
                }`}
                data-testid={`plan-${key}`}
              >
                    {plan.popular && !isCurrentPlan && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full whitespace-nowrap">
                    {t('premium.most_popular')}
                  </span>
                )}
                
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      key === 'gold' ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8962E]' : 'bg-gradient-to-br from-[#6E1A24] to-[#8A2331]'
                    }`}>
                      <PlanIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2D2D2A] text-base">
                        {plan.name}
                        {isCurrentPlan && <span className="ml-2 text-xs text-[#6C6C67] font-normal">{t('premium.current_plan')}</span>}
                      </h3>
                      <p className="text-xs text-[#6C6C67] mb-2">{plan.tagline}</p>
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-2xl font-bold text-[#2D2D2A]">{'\u20AC'}{plan.price}</span>
                        <span className="text-xs text-[#6C6C67]">{plan.period}</span>
                      </div>
                    </div>
                  </div>

                  {/* Radio */}
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected && !isCurrentPlan ? key === 'gold' ? 'border-[#D4AF37]' : 'border-[#6E1A24]' : 'border-[#D5D3CB]'
                  }`}>
                    {isSelected && !isCurrentPlan && (
                      <div className={`w-3 h-3 rounded-full ${key === 'gold' ? 'bg-[#D4AF37]' : 'bg-[#6E1A24]'}`} />
                    )}
                  </div>
                </div>

                {/* Benefits */}
                <div className="mt-3 space-y-1.5 pl-14">
                  {plan.benefits.map((b) => {
                    const BIcon = b.icon;
                    return (
                      <div key={b.text} className="flex items-center gap-2 text-xs">
                        <BIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: plan.color }} />
                        <span className={b.bold ? 'font-medium text-[#2D2D2A]' : 'text-[#6C6C67]'}>{b.text}</span>
                      </div>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>

        {/* Social proof + CTA */}
        <div className="px-5 pb-6 space-y-4">
          {/* Social proof line */}
          <div className="flex items-center justify-center gap-2 text-xs text-[#6C6C67]">
            <Users className="w-3.5 h-3.5" />
            <span>{t('premium.social_proof')}</span>
          </div>

          <Button
            onClick={handleUpgrade}
            disabled={currentPlan === selectedPlan}
            className={`w-full rounded-full py-4 text-base font-semibold transition-all hover:shadow-lg disabled:opacity-50 ${
              selectedPlan === 'gold'
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:from-[#C9A432] hover:to-[#A88929] text-white'
                : 'bg-[#6E1A24] hover:bg-[#8A2331] text-white'
            }`}
            data-testid="upgrade-btn"
          >
            {t('premium.upgrade_to', { plan: PLANS[selectedPlan].name, price: PLANS[selectedPlan].price })}
          </Button>

          {/* Urgency */}
          <p className="text-center text-[11px] text-[#8A8A84] leading-relaxed">
            {t('premium.profiles_get_more', { plan: PLANS[selectedPlan].name })}
          </p>
        </div>
      </div>
    </div>
  );
}
